/**
 * Configured values these surfaces draw that **no route an operator can read
 * publishes**.
 *
 * `GET /api/v1/admin/configurations` is `admin`-gated on every service, and
 * three of the four content routes admit an `operator`. So the settings below
 * are seeded **from the feature catalog's «Configuration, not features» table**
 * — the product's own declared values — with each one citing the row it was
 * read from. That is a catalog read rather than a hard-code, and the citation is
 * what makes the next edit traceable.
 *
 * **Returning an empty set instead would not have been the safe option.** An
 * empty set is itself an invented value — the one the product never chose — it
 * silently deletes a designed control, and on screen it is indistinguishable
 * from a failed read.
 *
 * **This module is temporary.** The moment an operator-readable vocabulary route
 * lands — the shape `GET /api/v1/caseStates` already has on `backend-support` —
 * every export here is replaced by a query and the file is deleted. Until then
 * it is the one named place these values live, rather than six literals spread
 * across four surfaces.
 */

/**
 * Catalog row «Content types» — *Film, Series. Two types, one schema each.*
 *
 * The order is the row's own. A third member is a catalog edit, not a release.
 *
 * **Lower-case, because these are the owning service's tokens rather than this
 * console's labels.** `ContentTypeEnum` spells them `film` and `series`, and the
 * `CONTENT_TYPES` setting mirrors that spelling. Sent upper-cased, every create
 * was refused with `CONTENT_METADATA_SCHEMA_INVALID` — a code naming the metadata
 * schema, which was the one part of the body that was correct.
 */
export const CONTENT_TYPES = ['film', 'series'] as const;

/**
 * Catalog row «`required_metadata`» — *title, cover, description, production
 * year, producing country, age rating, genre. The minimum before publication.*
 *
 * The tokens are the wire's; the wording is in each surface's own `copy.ts`.
 */
export const REQUIRED_METADATA = [
  'title',
  'cover',
  'description',
  'production_year',
  'producing_country',
  'age_rating',
  'genre',
] as const;

/**
 * Catalog row «`credit_roles`» — *director, producer, writer, actor, composer,
 * cinematographer. The permitted credit vocabulary.*
 *
 * **LOWERCASE, because that is the token the owning service configures and
 * stores.** This list read `DIRECTOR` and the six chips on the title's authoring
 * surface therefore wrote a token no configuration contained: `backend-content`
 * validates `role` against its own `CREDIT_ROLES` row and answered
 * `422 CONTENT_CREDIT_ROLE_NOT_CONFIGURED` to every press, so **no credit could
 * be added from a title at all**. The same mismatch made every chip read
 * unpressed against a credit the provider fill had created as `director`, which
 * would have added a duplicate rather than toggling the record.
 *
 * It is the **fallback** now rather than the vocabulary: the members are read
 * from the service at runtime, exactly as the credited-person surface reads
 * them, and this list stands in only where an `operator` meets the `403` that
 * the `admin`-gated configuration route answers them with.
 */
export const CREDIT_ROLES = [
  'director',
  'producer',
  'writer',
  'actor',
  'composer',
  'cinematographer',
] as const;

/**
 * Catalog row «External metadata provider» — *IMDb. The bound provider for §4's
 * assisted enrichment.*
 *
 * Rendered as an identifier, in the mono face, as issued.
 */
export const EXTERNAL_METADATA_PROVIDER = 'IMDb';

/**
 * **The age rating has no vocabulary anywhere, and that is why this surface
 * draws a field rather than a list.**
 *
 * `required_metadata` names *age rating* as a required field, and no row of the
 * configuration table declares its members — the value is type-specific metadata
 * validated against the type's own schema, which lives in code inside the
 * owning service and is published on no route this console can read.
 *
 * Offering a closed list here would be inventing a set the product has not
 * chosen; offering an empty one would delete the control. So the field is free
 * text, the owning service validates it against the type's schema, and its
 * refusal is what reaches the operator. **When a vocabulary route lands, this
 * becomes a select and the flag comes out.**
 */
export const AGE_RATING_HAS_PUBLISHED_VOCABULARY = false;

/** The configuration key the credit vocabulary lives under, on `backend-content`. */
export const CREDIT_ROLES_KEY = 'CREDIT_ROLES';
